import {
  Badge,
  Box,
  Button,
  CloseButton,
  Divider,
  Flex,
  Grid,
  Group,
  rem,
  Select,
  Text,
  TextInput,
} from "@mantine/core";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import React, { forwardRef, useEffect, useRef, useState } from "react";
import swal from "sweetalert";
import { useSelector } from "react-redux";
import {
  forgetPassword,
  getAllFacilities,
  resetStaff2FA,
} from "../../services";
import { removeStaff } from "../../services/staff";
import { removeContractor } from "../../services/auth";
import useStyles from "./style";
import ActionButtons from "./ActionButtons";
import { RiDeleteBin7Line } from "react-icons/ri";
import { useNavigate } from "react-router-dom";
import { DropDownArrowSvg } from "../Svgs/DropDownArrowSvg";
import delete2FAIcon from "../../images/alert2fa.png";
import success2FAIcon from "../../images/hugeiconsnoteremove.png";
import SetupTwoFactorModal from "../SetupTwoFactorModal";
/**
 * StaffDetailsTab component
 *
 * This component is used to display and edit staff details. It
 * includes a form with fields for first name, last name, job title,
 * phone number, email, and facility. It also includes a component
 * for adding and removing facilities.
 *
 * @param {object} data - The staff data object
 * @param {string} mode - The mode of the component, either "add", "edit", or "view"
 * @param {object} form - The form object from Mantine's useForm hook
 * @param {function} setSelectedStaff - A function to set the selected staff data object
 * @param {function} setAddedFacilities - A function to set the added facilities array
 * @param {array} addedFacilities - The added facilities array
 * @param {function} handleSave - A function to handle the form save action
 * @param {function} handleCancel - A function to handle the form cancel action
 */
export const StaffDetailsTab = ({
  data,
  mode,
  form,
  setSelectedStaff,
  handleSave,
  handleCancel,
}) => {
  const { classes } = useStyles();
  const isMobile = useMediaQuery("(max-width: 600px)");
  const navigate = useNavigate();
  const { profile } = useSelector((state) => {
    return state?.profile;
  });
  const [
    setupTwoFactorModalOpened,
    { open: setupTwoFactorModalOpen, close: setupTwoFactorModalClose },
  ] = useDisclosure(false);

  const resetPassword = async () => {
    const email = await swal({
      title: `Reset Password`,
      text: "Do you want to email reset password link?",
      dangerMode: false,
      buttons: ["Cancel", "Send"],
    });
    if (email) {
      forgetPassword({ email: data?.email }, "reset");
    }
  };

  const handleResetStaff2FA = async () => {
    const response = await resetStaff2FA({ staffId: data?.id });
    if (response) {
      success2FA();
      navigate("/home/staff");
    }
  };
  const reset2FA = async () => {
    const confirmed = await swal({
      title: "Reset two factor phone #?",
      text: "Are you sure you want to reset the phone number? This action cannot be undone.",
      icon: delete2FAIcon,
      buttons: {
        cancel: {
          text: "Cancel",
          visible: true,
          className: "swal-button--cancel",
        },
        confirm: {
          text: "Reset",
          visible: true,
          className: "swal-button--danger",
        },
      },
      dangerMode: true,
    });

    if (confirmed) {
      handleResetStaff2FA();
    }
  };

  const success2FA = async () => {
    const email = await swal({
      title: "Done!",
      text: "You have successfully reset phone number for two-factor authentication.",
      icon: success2FAIcon,
      buttons: {
        confirm: {
          text: "OK",
          visible: true,
          className: classes.customokbutton,
        },
      },
      dangerMode: false,
    });
  };

  const deleteStaffHandle = async () => {
    const isContractor = data?.type?.toLowerCase() === "contractor";
    const willDelete = await swal({
      title: `Are you sure?`,
      text: isContractor
        ? "This will unlink the contractor from your account. This action cannot be undone."
        : "This action cannot be undone.",
      icon: "warning",
      dangerMode: true,
      buttons: ["Cancel", isContractor ? "Remove" : "Delete"],
    });
    if (willDelete) {
      if (isContractor) {
        const success = await removeContractor(data?.id);
        if (success) navigate("/home/staff");
      } else {
        removeStaff(data?.id, () => {}, null);
        navigate("/home/staff");
      }
    }
  };

  return (
    <>
      <Box className={classes.StaffDetailsTab}>
        {mode !== "add" && (
          <>
            <Flex justify={"space-between"}>
              {data?.type?.toLowerCase() !== "contractor" ? (
                <Box className={classes.resetauthbox}>
                  <Button
                    className={classes.resetauth}
                    variant="transparent"
                    onClick={reset2FA}
                    // disabled={mode === "view"}
                  >
                    Reset Two Factor Phone #?
                  </Button>
                </Box>
              ) : <Box></Box>}

              <Flex justify={"flex-end"}>
                {data?.type?.toLowerCase() !== "contractor" && (
                  <Button
                    className={classes.password}
                    variant="transparent"
                    onClick={resetPassword}
                  >
                    Reset Password?
                  </Button>
                )}
                <Button
                  className={classes.delete}
                  variant="transparent"
                  color="gray"
                  c={"red"}
                  onClick={deleteStaffHandle}
                >
                  <RiDeleteBin7Line size={18} />
                  <span style={{ marginLeft: "5px" }}>
                    {data?.type?.toLowerCase() === "contractor" ? "Remove Contractor" : "Delete"}
                  </span>
                </Button>
              </Flex>
            </Flex>
            <Divider my={10} />
          </>
        )}
        <form onSubmit={form.onSubmit(handleSave)} className={classes.formBox}>
          <Grid mt={10}>
            <Grid.Col span={isMobile ? 12 : 6}>
              <TextInput
                label="First Name"
                placeholder="Enter First Name"
                radius="md"
                withAsterisk
                disabled={mode === "view"}
                {...form.getInputProps("firstName")}
                onChange={(e) => {
                  form.setFieldValue("firstName", e?.target?.value);
                  setSelectedStaff((prev) => {
                    return { ...prev, firstName: e?.target?.value };
                  });
                }}
              />
            </Grid.Col>
            <Grid.Col span={isMobile ? 12 : 6}>
              <TextInput
                label="Last Name"
                placeholder="Enter Last Name"
                radius="md"
                withAsterisk
                disabled={mode === "view"}
                {...form.getInputProps("lastName")}
                onChange={(e) => {
                  form.setFieldValue("lastName", e?.target?.value);
                  setSelectedStaff((prev) => {
                    return { ...prev, lastName: e?.target?.value };
                  });
                }}
              />
            </Grid.Col>
            <Grid.Col span={isMobile ? 12 : 6}>
              <TextInput
                label="Job Title"
                placeholder="Enter Job Title"
                type="text"
                radius="md"
                withAsterisk
                disabled={mode === "view"}
                {...form.getInputProps("position")}
              />
            </Grid.Col>
            <Grid.Col span={isMobile ? 12 : 6}>
              <TextInput
                label="Phone Number"
                placeholder="Enter Phone Number"
                type="number"
                radius="md"
                disabled={mode === "view"}
                {...form.getInputProps("phone")}
              />
            </Grid.Col>
            <Grid.Col span={isMobile ? 12 : 12}>
              <TextInput
                label="Email"
                placeholder="Enter Email"
                type="text"
                radius="md"
                withAsterisk
                disabled={mode === "view" || mode === "edit"}
                {...form.getInputProps("email")}
              />
            </Grid.Col>
          </Grid>
          {(mode === "add" || mode === "edit") && (
            <>
              <Divider my={10} />
              <Box pt={20}>
                <ActionButtons
                  onCancel={handleCancel}
                  type="submit"
                  onSave={handleSave}
                />
              </Box>
            </>
          )}
        </form>
      </Box>

      <SetupTwoFactorModal
        opened={setupTwoFactorModalOpened}
        close={setupTwoFactorModalClose}
        profile={profile}
        isSettingPage={true}
        staff={data}
        isStaffPage={true}
      />
    </>
  );
};
